<script setup lang="ts">
  import { computed, getCurrentInstance, nextTick, ref } from 'vue';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import {
    IDestinationSelection,
    ILogicalPrinter,
    PrinterCutMode,
    applyAssignmentSelection,
  } from '@/stores/printing-v2.config';
  import {
    V2_CODEPAGES,
    useV2PrintingUi,
  } from '@/composition/printing/use-printing-v2-ui';
  import {
    IEscposPrinterSettings,
    IZplPrinterSettings,
    PrinterCutType,
    PrinterModel,
    QrRenderMode,
    ZplDpmm,
    normalizeEscposSettings,
    normalizeZplSettings,
  } from '@/lib/print/printer-settings';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiListbox from '@/components/pos-v2/ui/PosUiListbox.vue';
  import V2PrinterAssignmentPicker from './V2PrinterAssignmentPicker.vue';

  const props = defineProps<{
    logicalId: number;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const ui = useUi();
  const {
    v2Store,
    printingStore,
    canEdit,
    isConnected,
    printCodepageMegaTest,
    printQrTest,
    printZplCalibration,
  } = useV2PrintingUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const logical = computed<ILogicalPrinter | null>(
    () =>
      (v2Store.config?.logicalPrinters ?? []).find(
        (lp) => lp.id === props.logicalId
      ) ?? null
  );

  function currentSelection(): IDestinationSelection[] {
    const doc = v2Store.config;
    if (!doc) return [];
    const result: IDestinationSelection[] = [];
    if (doc.assignments.ticket === props.logicalId) {
      result.push({ destination: 'ticket' });
    }
    for (const key of Object.keys(doc.assignments.comandaByStation)) {
      const stationId = Number(key);
      if (doc.assignments.comandaByStation[stationId] === props.logicalId) {
        result.push({ destination: 'comanda', stationId });
      }
    }
    if (doc.assignments.sticker === props.logicalId) {
      result.push({ destination: 'sticker' });
    }
    return result;
  }

  // #region form state (seeded once from the logical printer at open)

  const name = ref(logical.value?.name ?? '');
  const selection = ref<IDestinationSelection[]>(currentSelection());
  const saving = ref(false);

  // ESC/POS hardware fields.
  const paperWidthMm = ref<58 | 80>(logical.value?.paperWidthMm ?? 80);
  const codepage = ref(logical.value?.codepage ?? 'cp858');
  const model = ref<PrinterModel>(logical.value?.model ?? 'generic');
  const qrMode = ref<QrRenderMode>(logical.value?.qrMode ?? 'auto');
  const cutType = ref<PrinterCutType>(logical.value?.cutType ?? 'partial');
  const cutMode = ref<PrinterCutMode>(logical.value?.cutMode ?? 'modern');
  // Empty = per-document legacy feed (byte parity for untouched printers).
  const feedLines = ref(
    logical.value?.feedLines != null ? String(logical.value.feedLines) : ''
  );
  const charsNormal = ref(
    logical.value?.charsPerLineNormal != null
      ? String(logical.value.charsPerLineNormal)
      : ''
  );
  const charsSmall = ref(
    logical.value?.charsPerLineSmall != null
      ? String(logical.value.charsPerLineSmall)
      : ''
  );
  const advancedOpen = ref(
    logical.value?.charsPerLineNormal != null ||
      logical.value?.charsPerLineSmall != null
  );

  // ZPL fields: explicit settings win, else the same fallback chain print
  // time uses (legacy localStorage stickerSettings → defaults).
  const seededZpl: IZplPrinterSettings = logical.value?.zpl
    ? normalizeZplSettings(logical.value.zpl)
    : printingStore.printerSettingsOf(
        logical.value
          ? { id: logical.value.ref, name: logical.value.name, type: 'usb' }
          : null
      ).zpl;

  const dpmm = ref<ZplDpmm>(seededZpl.dpmm);
  const zplWidthMm = ref(String(seededZpl.widthMm));
  const zplHeightMm = ref(String(seededZpl.heightMm));
  const padTop = ref(String(seededZpl.padding.top));
  const padRight = ref(String(seededZpl.padding.right));
  const padBottom = ref(String(seededZpl.padding.bottom));
  const padLeft = ref(String(seededZpl.padding.left));
  const zplFontSize = ref<IZplPrinterSettings['fontSize']>(seededZpl.fontSize);
  const offsetXmm = ref(String(seededZpl.offsetXmm));
  const offsetYmm = ref(String(seededZpl.offsetYmm));

  // #endregion

  // #region numeric parsing (normalizers clamp; empty = null/auto)

  function toIntOrNull(raw: string): number | null {
    const value = Number(raw.trim());
    return raw.trim() !== '' && Number.isFinite(value) ? value : null;
  }

  function toNumberOr(raw: string, fallback: number): number {
    const value = Number(raw.trim());
    return raw.trim() !== '' && Number.isFinite(value) ? value : fallback;
  }

  /** Current form values as a normalized ESC/POS settings object. */
  function formEscpos(): IEscposPrinterSettings {
    return normalizeEscposSettings({
      model: model.value,
      codepage: codepage.value,
      paperWidthMm: paperWidthMm.value,
      qrMode: qrMode.value,
      charsPerLineNormal: toIntOrNull(charsNormal.value),
      charsPerLineSmall: toIntOrNull(charsSmall.value),
      cutType: cutType.value,
      cutMode: cutMode.value,
      feedLines: toIntOrNull(feedLines.value),
    });
  }

  /** Current form values as a normalized ZPL settings object. */
  function formZpl(): IZplPrinterSettings {
    return normalizeZplSettings({
      dpmm: dpmm.value,
      widthMm: toNumberOr(zplWidthMm.value, seededZpl.widthMm),
      heightMm: toNumberOr(zplHeightMm.value, seededZpl.heightMm),
      padding: {
        top: toNumberOr(padTop.value, seededZpl.padding.top),
        right: toNumberOr(padRight.value, seededZpl.padding.right),
        bottom: toNumberOr(padBottom.value, seededZpl.padding.bottom),
        left: toNumberOr(padLeft.value, seededZpl.padding.left),
      },
      fontSize: zplFontSize.value,
      offsetXmm: toNumberOr(offsetXmm.value, 0),
      offsetYmm: toNumberOr(offsetYmm.value, 0),
    });
  }

  // #endregion

  // #region listbox items + typed input handlers

  /** Narrows a listbox emit to a whitelisted union member (else fallback). */
  function pickOption<T extends string | number>(
    value: number | string | null,
    allowed: readonly T[],
    fallback: T
  ): T {
    return (allowed as readonly (string | number)[]).includes(
      value as string | number
    )
      ? (value as T)
      : fallback;
  }

  const MODEL_OPTIONS: readonly PrinterModel[] = [
    'generic',
    'genericb',
    'epsona',
  ];
  const QR_MODE_OPTIONS: readonly QrRenderMode[] = ['auto', 'native', 'image'];
  const CUT_TYPE_OPTIONS: readonly PrinterCutType[] = ['partial', 'full'];
  const CUT_MODE_OPTIONS: readonly PrinterCutMode[] = ['modern', 'legacy'];
  const DPMM_OPTIONS: readonly ZplDpmm[] = [6, 8, 12, 24];
  const FONT_SIZE_OPTIONS: readonly IZplPrinterSettings['fontSize'][] = [
    'xs',
    's',
    'm',
    'l',
  ];

  const codepageItems = V2_CODEPAGES.map((cp) => ({ id: cp, label: cp }));

  const modelItems = computed(() => [
    { id: 'generic', label: i18nT('printingV2.modelGeneric') },
    { id: 'genericb', label: i18nT('printingV2.modelGenericB') },
    { id: 'epsona', label: i18nT('printingV2.modelEpsonA') },
  ]);

  const qrModeItems = computed(() => [
    { id: 'auto', label: i18nT('printingV2.qrModeAuto') },
    { id: 'native', label: i18nT('printingV2.qrModeNative') },
    { id: 'image', label: i18nT('printingV2.qrModeImage') },
  ]);

  const qrModeHelp = computed(() => {
    if (qrMode.value === 'native') return i18nT('printingV2.qrModeNativeHelp');
    if (qrMode.value === 'image') return i18nT('printingV2.qrModeImageHelp');
    return i18nT('printingV2.qrModeAutoHelp');
  });

  const cutTypeItems = computed(() => [
    { id: 'partial', label: i18nT('printingV2.cutTypePartial') },
    { id: 'full', label: i18nT('printingV2.cutTypeFull') },
  ]);

  const cutModeItems = computed(() => [
    { id: 'modern', label: i18nT('printingV2.cutModeModern') },
    { id: 'legacy', label: i18nT('printingV2.cutModeLegacy') },
  ]);

  const dpmmItems = [6, 8, 12, 24].map((value) => ({
    id: value,
    label: `${value} dpmm`,
  }));

  const fontSizeItems = computed(() => [
    { id: 'xs', label: i18nT('printingV2.zplFontXs') },
    { id: 's', label: i18nT('printingV2.zplFontS') },
    { id: 'm', label: i18nT('printingV2.zplFontM') },
    { id: 'l', label: i18nT('printingV2.zplFontL') },
  ]);

  // #endregion

  /** Live dots hint for the ZPL geometry (round(mm*dpmm)). */
  const zplWidthDots = computed(() =>
    Math.round(toNumberOr(zplWidthMm.value, 0) * dpmm.value)
  );
  const zplHeightDots = computed(() =>
    Math.round(toNumberOr(zplHeightMm.value, 0) * dpmm.value)
  );

  const canSave = computed(
    () =>
      canEdit.value &&
      !saving.value &&
      !!logical.value &&
      name.value.trim().length > 0
  );

  // #region test prints (form values, so the user can iterate before saving)

  const testingCodes = ref(false);
  const testingQr = ref(false);
  const testingCalibration = ref(false);

  // Close/cancel stay disabled while ANY async flow is in flight: runTest's
  // finally resets its flag, and that write must never land mid-teardown
  // (vue-renderer-safety) — so teardown can't start while a test runs.
  const busy = computed(
    () =>
      saving.value ||
      testingCodes.value ||
      testingQr.value ||
      testingCalibration.value
  );

  async function runTest(
    flag: typeof testingCodes,
    action: () => Promise<void>
  ) {
    if (flag.value || !logical.value) return;
    flag.value = true;
    try {
      await action();
      ui.showToast(AlertType.Success, i18nT('printingV2.testSent'));
    } catch (e) {
      console.error('[PrintingV2] test print failed', e);
      ui.showToast(AlertType.Error, i18nT('printingV2.testFailed'));
    } finally {
      flag.value = false;
    }
  }

  function testCodepages() {
    void runTest(testingCodes, () =>
      printCodepageMegaTest(
        logical.value!,
        formEscpos(),
        i18nT('printingV2.kindTest')
      )
    );
  }

  function testQr() {
    void runTest(testingQr, () =>
      printQrTest(logical.value!, formEscpos(), i18nT('printingV2.kindTest'))
    );
  }

  function testZplCalibration() {
    void runTest(testingCalibration, () =>
      printZplCalibration(
        logical.value!,
        formZpl(),
        i18nT('printingV2.kindTest')
      )
    );
  }

  // #endregion

  async function save() {
    if (!canSave.value) return;
    saving.value = true;
    // Renderer-safety: don't mutate local reactive state after emit('close')
    // starts the modal teardown — only reset the spinner when staying open.
    let closed = false;
    try {
      // Snapshot the normalized forms OUTSIDE the mutator: mutateConfig may
      // retry the draft function and it must stay pure.
      const escpos = logical.value?.format === 'escpos' ? formEscpos() : null;
      const zpl = logical.value?.format === 'zpl' ? formZpl() : null;
      const committed = await v2Store.mutateConfig((draft) => {
        const target = draft.logicalPrinters.find(
          (lp) => lp.id === props.logicalId
        );
        if (!target) return;
        target.name = name.value.trim();
        if (target.format === 'escpos' && escpos) {
          target.paperWidthMm = escpos.paperWidthMm;
          target.codepage = escpos.codepage;
          target.model = escpos.model;
          target.qrMode = escpos.qrMode;
          target.charsPerLineNormal = escpos.charsPerLineNormal;
          target.charsPerLineSmall = escpos.charsPerLineSmall;
          target.cutType = escpos.cutType;
          target.cutMode = escpos.cutMode;
          target.feedLines = escpos.feedLines;
        }
        if (target.format === 'zpl' && zpl) {
          target.zpl = zpl;
        }
        applyAssignmentSelection(draft, props.logicalId, selection.value);
      });
      if (committed) {
        ui.showToast(AlertType.Success, i18nT('printingV2.saved'));
        // Let the save's re-renders flush BEFORE starting the modal's leave
        // transition: sharing a flush with the write burst can trip Vue's
        // circular-update breaker and strand the modal half-closed.
        await nextTick();
        closed = true;
        emit('close');
      }
    } finally {
      if (!closed) saving.value = false;
    }
  }
</script>

<template>
  <div class="v2-printer-edit-modal">
    <div class="edit-header">
      <h3 class="edit-title">{{ $t('printingV2.editTitle') }}</h3>
      <button
        class="edit-close"
        type="button"
        :disabled="busy"
        @click="emit('close')"
      >
        <PosUiIcon name="close" :size="20" />
      </button>
    </div>

    <div v-if="logical" class="edit-body">
      <div class="edit-field">
        <label class="edit-label">{{ $t('printingV2.fieldName') }}</label>
        <PosUiInput
          :value="name"
          :maxlength="40"
          :disabled="!canEdit"
          @input="name = $event"
        />
      </div>

      <!-- ESC/POS hardware section -->
      <template v-if="logical.format === 'escpos'">
        <div class="edit-section-title">
          {{ $t('printingV2.sectionHardware') }}
        </div>

        <div class="edit-field">
          <label class="edit-label">{{ $t('printingV2.fieldPaper') }}</label>
          <div class="edit-paper-toggle">
            <button
              type="button"
              class="paper-option"
              :class="{ active: paperWidthMm === 80 }"
              :disabled="!canEdit"
              @click="paperWidthMm = 80"
            >
              {{ $t('printingV2.paper80') }}
            </button>
            <button
              type="button"
              class="paper-option"
              :class="{ active: paperWidthMm === 58 }"
              :disabled="!canEdit"
              @click="paperWidthMm = 58"
            >
              {{ $t('printingV2.paper58') }}
            </button>
          </div>
        </div>

        <div class="edit-field">
          <label class="edit-label">{{ $t('printingV2.fieldModel') }}</label>
          <PosUiListbox
            :value="model"
            :items="modelItems"
            :disabled="!canEdit"
            @input="model = pickOption($event, MODEL_OPTIONS, 'generic')"
          />
        </div>

        <div class="edit-field">
          <label class="edit-label">{{ $t('printingV2.fieldCodepage') }}</label>
          <PosUiListbox
            :value="codepage"
            :items="codepageItems"
            searchable
            :disabled="!canEdit"
            @input="codepage = String($event ?? 'cp858')"
          />
          <div class="edit-test-row">
            <PosUiButton
              size="sm"
              color="neutral"
              variant="outline"
              icon="text-search"
              :loading="testingCodes"
              :disabled="!isConnected"
              @click="testCodepages"
            >
              {{ $t('printingV2.testCodepages') }}
            </PosUiButton>
            <PosUiButton
              size="sm"
              color="neutral"
              variant="outline"
              icon="qrcode"
              :loading="testingQr"
              :disabled="!isConnected"
              @click="testQr"
            >
              {{ $t('printingV2.testQr') }}
            </PosUiButton>
          </div>
        </div>

        <div class="edit-field">
          <label class="edit-label">{{ $t('printingV2.fieldQrMode') }}</label>
          <PosUiListbox
            :value="qrMode"
            :items="qrModeItems"
            :disabled="!canEdit"
            @input="qrMode = pickOption($event, QR_MODE_OPTIONS, 'auto')"
          />
          <p class="edit-hint">{{ qrModeHelp }}</p>
        </div>

        <div class="edit-field-row">
          <div class="edit-field">
            <label class="edit-label">{{ $t('printingV2.fieldCutType') }}</label>
            <PosUiListbox
              :value="cutType"
              :items="cutTypeItems"
              :disabled="!canEdit"
              @input="cutType = pickOption($event, CUT_TYPE_OPTIONS, 'partial')"
            />
          </div>
          <div class="edit-field">
            <label class="edit-label">{{ $t('printingV2.fieldCutMode') }}</label>
            <PosUiListbox
              :value="cutMode"
              :items="cutModeItems"
              :disabled="!canEdit"
              @input="cutMode = pickOption($event, CUT_MODE_OPTIONS, 'modern')"
            />
          </div>
          <div class="edit-field">
            <label class="edit-label">{{
              $t('printingV2.fieldFeedLines')
            }}</label>
            <PosUiInput
              :value="feedLines"
              type="number"
              inputmode="numeric"
              :placeholder="$t('printingV2.charsAuto')"
              :disabled="!canEdit"
              @input="feedLines = $event"
            />
          </div>
        </div>

        <!-- Collapsible advanced calibration -->
        <button
          type="button"
          class="edit-collapse-toggle"
          @click="advancedOpen = !advancedOpen"
        >
          <PosUiIcon
            :name="advancedOpen ? 'chevron-down' : 'chevron-right'"
            :size="18"
          />
          {{ $t('printingV2.sectionAdvancedCalibration') }}
        </button>
        <div v-if="advancedOpen" class="edit-advanced">
          <div class="edit-field-row">
            <div class="edit-field">
              <label class="edit-label">{{
                $t('printingV2.fieldCharsNormal')
              }}</label>
              <PosUiInput
                :value="charsNormal"
                type="number"
                inputmode="numeric"
                :placeholder="$t('printingV2.charsAuto')"
                :disabled="!canEdit"
                clearable
                @input="charsNormal = $event"
              />
              <p class="edit-hint">{{ $t('printingV2.charsNormalHint') }}</p>
            </div>
            <div class="edit-field">
              <label class="edit-label">{{
                $t('printingV2.fieldCharsSmall')
              }}</label>
              <PosUiInput
                :value="charsSmall"
                type="number"
                inputmode="numeric"
                :placeholder="$t('printingV2.charsAuto')"
                :disabled="!canEdit"
                clearable
                @input="charsSmall = $event"
              />
              <p class="edit-hint">{{ $t('printingV2.charsSmallHint') }}</p>
            </div>
          </div>
        </div>
      </template>

      <!-- ZPL panel -->
      <template v-else>
        <div class="edit-section-title">
          {{ $t('printingV2.sectionZpl') }}
        </div>

        <div class="edit-field-row">
          <div class="edit-field">
            <label class="edit-label">{{ $t('printingV2.fieldDensity') }}</label>
            <PosUiListbox
              :value="dpmm"
              :items="dpmmItems"
              :disabled="!canEdit"
              @input="dpmm = pickOption($event, DPMM_OPTIONS, 12)"
            />
          </div>
          <div class="edit-field">
            <label class="edit-label">{{
              $t('printingV2.fieldZplWidth')
            }}</label>
            <PosUiInput
              :value="zplWidthMm"
              type="number"
              inputmode="decimal"
              suffix="mm"
              :disabled="!canEdit"
              @input="zplWidthMm = $event"
            />
            <p class="edit-hint">
              {{ $t('printingV2.zplDotsHint', { dots: zplWidthDots }) }}
            </p>
          </div>
          <div class="edit-field">
            <label class="edit-label">{{
              $t('printingV2.fieldZplHeight')
            }}</label>
            <PosUiInput
              :value="zplHeightMm"
              type="number"
              inputmode="decimal"
              suffix="mm"
              :disabled="!canEdit"
              @input="zplHeightMm = $event"
            />
            <p class="edit-hint">
              {{ $t('printingV2.zplDotsHint', { dots: zplHeightDots }) }}
            </p>
          </div>
        </div>

        <div class="edit-field">
          <label class="edit-label">{{ $t('printingV2.fieldZplMargins') }}</label>
          <div class="edit-field-row">
            <div class="edit-field">
              <PosUiInput
                :value="padTop"
                type="number"
                inputmode="numeric"
                :prefix="$t('printingV2.marginTop')"
                :disabled="!canEdit"
                @input="padTop = $event"
              />
            </div>
            <div class="edit-field">
              <PosUiInput
                :value="padRight"
                type="number"
                inputmode="numeric"
                :prefix="$t('printingV2.marginRight')"
                :disabled="!canEdit"
                @input="padRight = $event"
              />
            </div>
            <div class="edit-field">
              <PosUiInput
                :value="padBottom"
                type="number"
                inputmode="numeric"
                :prefix="$t('printingV2.marginBottom')"
                :disabled="!canEdit"
                @input="padBottom = $event"
              />
            </div>
            <div class="edit-field">
              <PosUiInput
                :value="padLeft"
                type="number"
                inputmode="numeric"
                :prefix="$t('printingV2.marginLeft')"
                :disabled="!canEdit"
                @input="padLeft = $event"
              />
            </div>
          </div>
          <p class="edit-hint">{{ $t('printingV2.zplMarginsHint') }}</p>
        </div>

        <div class="edit-field-row">
          <div class="edit-field">
            <label class="edit-label">{{
              $t('printingV2.fieldZplFontSize')
            }}</label>
            <PosUiListbox
              :value="zplFontSize"
              :items="fontSizeItems"
              :disabled="!canEdit"
              @input="zplFontSize = pickOption($event, FONT_SIZE_OPTIONS, 'm')"
            />
          </div>
          <div class="edit-field">
            <label class="edit-label">{{ $t('printingV2.fieldOffsetX') }}</label>
            <PosUiInput
              :value="offsetXmm"
              type="number"
              inputmode="decimal"
              suffix="mm"
              :disabled="!canEdit"
              @input="offsetXmm = $event"
            />
          </div>
          <div class="edit-field">
            <label class="edit-label">{{ $t('printingV2.fieldOffsetY') }}</label>
            <PosUiInput
              :value="offsetYmm"
              type="number"
              inputmode="decimal"
              suffix="mm"
              :disabled="!canEdit"
              @input="offsetYmm = $event"
            />
          </div>
        </div>
        <p class="edit-hint">{{ $t('printingV2.zplOffsetHelp') }}</p>

        <div class="edit-test-row">
          <PosUiButton
            size="sm"
            color="neutral"
            variant="outline"
            icon="ruler-square"
            :loading="testingCalibration"
            :disabled="!isConnected"
            @click="testZplCalibration"
          >
            {{ $t('printingV2.printCalibration') }}
          </PosUiButton>
        </div>
      </template>

      <div class="edit-field">
        <label class="edit-label">{{
          $t('printingV2.fieldAssignments')
        }}</label>
        <V2PrinterAssignmentPicker
          :value="selection"
          :logical-id="logicalId"
          :disabled="!canEdit"
          @input="selection = $event"
        />
      </div>
    </div>

    <div class="edit-footer">
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="busy"
        @click="emit('close')"
      >
        {{ $t('printingV2.cancel') }}
      </PosUiButton>
      <PosUiButton
        color="primary"
        :loading="saving"
        :disabled="!canSave"
        @click="save"
      >
        {{ $t('printingV2.save') }}
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-printer-edit-modal {
    @apply tw-flex tw-flex-col tw-bg-white;

    .edit-header {
      @apply tw-flex tw-items-center tw-justify-between tw-border-b tw-border-neutral-100 tw-px-5 tw-py-4;

      .edit-title {
        @apply tw-m-0 tw-text-base tw-font-bold tw-text-neutral-900;
      }

      .edit-close {
        @apply tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-400 hover:tw-bg-neutral-100 hover:tw-text-neutral-700;
      }
    }

    .edit-body {
      @apply tw-flex tw-flex-col tw-gap-4 tw-px-5 tw-py-4;

      .edit-section-title {
        @apply tw-mt-1 tw-border-t tw-border-neutral-100 tw-pt-3 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-neutral-400;
      }

      .edit-field {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1.5;

        .edit-label {
          @apply tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
        }

        .edit-paper-toggle {
          @apply tw-flex tw-gap-0.5 tw-self-start tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

          .paper-option {
            @apply tw-rounded-md tw-border tw-border-transparent tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors hover:tw-text-neutral-800;

            &.active {
              @apply tw-border-neutral-200 tw-bg-white tw-font-semibold tw-text-neutral-900 tw-shadow-sm;
            }

            &:disabled {
              @apply tw-cursor-not-allowed tw-opacity-50;
            }
          }
        }
      }

      .edit-field-row {
        @apply tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row;
      }

      .edit-hint {
        @apply tw-m-0 tw-text-xs tw-text-neutral-400;
      }

      .edit-test-row {
        @apply tw-flex tw-flex-wrap tw-gap-2 tw-pt-1;
      }

      .edit-collapse-toggle {
        @apply tw-flex tw-items-center tw-gap-1 tw-self-start tw-text-sm tw-font-medium tw-text-neutral-500 hover:tw-text-neutral-800;
      }

      .edit-advanced {
        @apply tw-rounded-lg tw-bg-neutral-50 tw-p-3;
      }
    }

    .edit-footer {
      @apply tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-neutral-100 tw-px-5 tw-py-4;
    }
  }
</style>
